import {Power3, TimelineLite, TimelineMax, TweenLite} from "gsap";
import {IReelStateEventData, ReelState} from "../../core/reel/ReelState";
import {TheRaveSymbol} from "../symbol/TheRaveSymbol";
import {TheRaveReelView} from "./TheRaveReelView";
import {MathHelper} from "../../core/utils/MathHelper";
import {TheRave} from "../TheRave";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {TheRaveGameConfig} from "../TheRaveGameConfig";
import {TheRaveSymbolConfig, TheRaveSymbolState} from "../symbol/TheRaveSymbolConfig";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {TheRaveSplitSymbol} from "../symbol/TheRaveSplitSymbol";
import {TheRaveXWaysSymbol} from "../symbol/TheRaveXWaysSymbol";
import {TheRaveWildSymbol} from "../symbol/TheRaveWildSymbol";
import {TheRaveScatterSymbol} from "../symbol/TheRaveScatterSymbol";
import {Layers, TheRaveLayers} from "../view/TheRaveLayers";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {ArrayHelper} from "../debug/utils/ArrayHelper";

export class TheRaveReelController {
    public readonly events: APIEventSystem;

    public reelId: number;
    public reelView: TheRaveReelView;
    public isQuickStop: boolean;

    private _superFsReel: boolean = false;
    private _readyToStopped: boolean = false;

    private readonly _symbolsNumInDataNormalReel: number[] = [0, 1, 3, 1, 0]; // see legacy doc wiki about reel config.
    private readonly _symbolsNumInDataExtraReel: number[] = [0, 1, 1, 0, 0]; // see legacy doc wiki about reel config.
    private _symbolsNumInData: number[]; // see legacy doc wiki about reel config.
    private _symsNumTotal: number;
    private _currentReelSet: string[]; //The reelSet currently in use, it might be different than the actually reelSet because its made of many different parts.
    private _currentReelSetPosition: number; // The current position in the reel
    private _currentFullReelSet: string[]; // The full actually reelSet that the most part of the _currentReelSet is make of.
    private _stopReelSetFilledBottomNum: number = 0; // Use to calculate added symbols under the server reel symbols when stopping.
    private _extraStopDelay: number;
    private _reelStopDelayTimer: TweenLite | null; //Delay for reel stop
    private _stopCounter: number = 0;

    private _normalStartStepDuration: number = 0.17;
    private _fastStartStepDuration: number = 0.1;
    private _startEase: string = `circ.in`;
    private _defaultSpinStepDuration: number = 0.04;
    private _normalStopStepDuration: number = 0.15;
    private _fastStopStepDuration: number = 0.1;
    private _linkedNum: number = 1;
    private _linkedMostLeftReel: number = -1;
    private _normalStopEase: string = "rough({ template: none.inOut, strength: 5, points: 20, taper: 'in', randomize: false, clamp: false})";
    private _fastStopEase: string = this._normalStopEase;
    protected _stepEase: string = "none";
    private _nextStopReel: number | undefined;

    private _reelState: ReelState;
    get reelState(): ReelState {
        return this._reelState;
    }

    get mostLeftReel(): number {
        return this._linkedMostLeftReel;
    }

    set reelState(value: ReelState) {
        if (value === this._reelState) {
            return;
        }

        const oldState = this._reelState;
        this._reelState = value;
        this.events.trigger("ReelStateChange", <IReelStateEventData>{
            reelId: this.reelId,
            newState: this._reelState,
            oldState: oldState
        });
    }

    constructor(reelId: number) {
        this.events = SlotGame.apiPlugin.eventSystemFactory.create();
        this.events.on("ReelStateChange", (data: IReelStateEventData) => this.events.trigger(data.newState, data)); //To prevent event system from saving once callbacks if no on callbacks is set.

        this.reelId = reelId;
        this.reelState = ReelState.IDLE;
        const isExtraReel: boolean = this.reelId > 4;
        this._symbolsNumInData = isExtraReel ? this._symbolsNumInDataExtraReel : this._symbolsNumInDataNormalReel;
        this._symsNumTotal = ArrayHelper.arraySum(this._symbolsNumInData);
        this.reelView = new TheRaveReelView(reelId);
        this.initSymbols();
    }

    private initSymbols(): void {
        this._currentFullReelSet = this.getReelSet(TheRave.model.parsedInitData.nextReelSetName);
        this._currentReelSet = this.makeUpStopReelSet(true, TheRave.model.reels[this.reelId].concat());
        if (this._stopReelSetFilledBottomNum < this._symbolsNumInData[4]) {
            this._currentReelSet = this._currentReelSet.concat(this.makeUpStopReelSetRandomBottomFillUp(this._symbolsNumInData[4] - this._stopReelSetFilledBottomNum));
        }
        this._currentReelSetPosition = this._currentReelSet.length;

        for (let i: number = 0; i < this._symsNumTotal; i++) {
            this.addNextNewSymbol(TheRaveSymbolState.NORMAL, true);
        }
        this.reelView.initSymbolsPosition(this._symbolsNumInData[0] + this._symbolsNumInData[1]);
    }

    public isBoostActive():boolean {
        return this.reelView.isBoostActive();
    }

    public enableBoostButton(value: boolean): void {
        this.reelView.enableButton(value);
    }

    public hideBoostButton(): void {
        this.reelView.hideBoostButton();
    }

    public showBoostButton(): void {
        this.reelView.showBoostButton();
    }


    private getReelSet(name: string): string[] {
        if (TheRaveGameConfig.REEL_SETS[name]) {
            const reelSet: string[] = TheRaveGameConfig.REEL_SETS[name][this.reelId];
            if (reelSet.length < 5) {
                Logger.logDev(`ReelController.getReelSet() name = ${name}, reelId = ${this.reelId}, the reelSet is too short that might break ReelController.randomizeReelSetPosition().`)
            }
            return reelSet;
        }

        throw new Error(`ReelController.getReelSet(): ReelSet ${name} is missing from GameConfig!`);
    }

    // only for spin here
    private makeUpStopReelSet(init: boolean, stopReelSet: string[]): string[] {
        const randomPosition: number = this.randomizeReelSetPosition(this._currentFullReelSet);
        // const bottomRandomFillUp:string[] = this.makeUpStopReelSetRandomBottomFillUp(bottomFillUpTotalNum - bottomFillUp.length, init);
        return this._currentFullReelSet.slice(0, randomPosition + 1).concat(stopReelSet);
    }

    // Get a position where the symbol is not part of a stacked symbol unless it's the bottom of the stacked symbol.
    private randomizeReelSetPosition(reelSet: string[]): number {
        let position: number = MathHelper.randomNumberInRange(4, reelSet.length - 1); // Start on 10 to make sure there is enough number included.
        return position;
    }

    private makeUpStopReelSetRandomBottomFillUp(length: number): string[] {
        return ArrayHelper.initArrayWithValues(length, index => this.getRandomSymbolNameFromReelSet(this._currentFullReelSet));
    }

    private getRandomSymbolNameFromReelSet(reelSet: string[]): string {
        let symbolName: string, isEnhancerActivate:boolean = false;
        if(TheRave.enhancerReels[this.reelId]){
            isEnhancerActivate = !TheRave.enhancerReels[this.reelId].reelView.isLocked;
        }
        do {
            symbolName = ArrayHelper.getRandomValue<string>(reelSet);
        } while (symbolName.includes("S") && isEnhancerActivate);

        return symbolName;
    }

    private addNextNewSymbol(state: TheRaveSymbolState, init: boolean = false): TheRaveSymbol {
        let symbolName: string, isEnhancerActivate:boolean = false;
        if(TheRave.enhancerReels[this.reelId]){
            isEnhancerActivate = !TheRave.enhancerReels[this.reelId].reelView.isLocked;
        }
        do {
            this.moveReelSetPosition();
            symbolName = this._currentReelSet[this._currentReelSetPosition];
        } while (symbolName === "S" && isEnhancerActivate);

        let symbol: TheRaveSymbol = this.createSymbol(symbolName, this.reelId, state, 150);

        this.reelView.addSymbol(symbol);
        this.reelView.allSymbols.unshift(symbol);


        const previousSymbol = this.reelView.allSymbols[1];
        //StackedSymbol.updateStackedSymbols(symbol, previousSymbol, -1, true);
        // TODO: Reverting pos switch, due to switching reels
        if (previousSymbol) {
            symbol.y = previousSymbol.y - previousSymbol.symbolHeight * 0.5 - symbol.bottomPadding - symbol.symbolHeight * 0.5;
        }
        //symbol.y = 0 - symbol.symbolHeight * 0.5;
        return symbol;
    }

    private createFullWildSymbol(): void {
        //const fullWild
    }

    public createSymbol(symName: string, reelId: number, state: TheRaveSymbolState, symHeight: number = 150, fullWild:boolean = false): TheRaveSymbol {
        if (symName === "XS") {
            return new TheRaveSplitSymbol(symName, this.reelId, state, symHeight);
        } else if (symName === "XW") {
            return new TheRaveXWaysSymbol(symName, this.reelId, state, symHeight, this._linkedNum);
        } else if (symName === "W") {
            return new TheRaveWildSymbol(symName, this.reelId, state, symHeight, undefined, fullWild);
        } else if (symName === "S") {
            return new TheRaveScatterSymbol(symName, this.reelId, state, symHeight);
        }
        return new TheRaveSymbol(symName, this.reelId, state, symHeight, this._linkedNum);
    }

    private removeBottomSymbol(): void {
        const removeSymbol = this.reelView.allSymbols.pop()!;
        this.reelView.removeSymbol(removeSymbol);
    }

    private moveReelSetPosition(): void {
        if (this._currentReelSetPosition == 0) {
            if (this._currentFullReelSet == null) {
                debugger;
                throw new Error("ReelController.createNextSymbol():This is a bug that Ning is trying to reproduce!");
            }
            this._currentReelSet = this._currentFullReelSet.concat();
        }
        this._currentReelSetPosition--;
        this._currentReelSetPosition = ArrayHelper.reviseIndexInLoopRange(this._currentReelSet.length, this._currentReelSetPosition);
    }

    private setNextState(reelState: ReelState, duration?: number, ease?: string): void {
        if (reelState === ReelState.SPIN) {
            ease = this._stepEase;
        }
        // Logger.logDev(`reel ${this.reelId} set next state ${reelState}.`);
        this.reelView.nextSpinStepConfig = {
            reelState: reelState,
            duration: duration,
            ease: ease
        }
    }

    public startSpin(active: boolean): TimelineLite {
        Logger.logDev(`reel ${this.reelId} startSpin.`);
        this._extraStopDelay = 0;
        this._stopCounter = 0;
        this.isQuickStop = false;
        this.reelView.wantBounce = false;
        this._linkedNum = 1;
        this._readyToStopped = false;
        if (!active) {
            this.reelState = ReelState.INACTIVE;
            return new TimelineLite();
        } else {
            this.setNextState(
                ReelState.START,
                TheRave.apiPlugin.settings.get(APISetting.FAST_SPIN) ? this._fastStartStepDuration : this._normalStartStepDuration,
                this._startEase
            );
            const tl = new TimelineLite({
                onComplete: () => {
                    if (active) {
                        // Logger.logDev(`reel id = ${this.reelId}, set spin state on start complete.`);
                        this.setNextState(ReelState.SPIN, this._defaultSpinStepDuration, 'none');
                        this.spinStep();
                    }
                }
            });
            tl.add(() => this.reelView.show());
            tl.add(this.reelView.step(this.addNextNewSymbol(TheRaveSymbolState.SPIN), () => this.removeBottomSymbol()));
            return tl;
        }
    }

    public spinStep(): TimelineLite {
        const tl = new TimelineLite({
            onComplete: () => {
                if (this.reelState === ReelState.SPIN) {
                    this.spinStep();
                    return;
                }
                if (this.reelState === ReelState.STOPPING) {
                    if (this._stopCounter > 1) {
                        this._stopCounter--;
                        this.spinStep();
                        return;
                    }

                    this._stopCounter = 0;
                    this.stopStep();
                }
            }
        });
        tl.add(this.reelView.step(this.addNextNewSymbol(TheRaveSymbolState.SPIN), () => this.removeBottomSymbol()));
        return tl;
    }

    public stopStep(): TimelineLite {
        this.setNextState(
            ReelState.STOPPING,
            (TheRave.apiPlugin.settings.get(APISetting.FAST_SPIN) || this.isQuickStop) ? this._fastStopStepDuration : this._normalStopStepDuration,
            (TheRave.apiPlugin.settings.get(APISetting.FAST_SPIN) || this.isQuickStop) ? this._fastStopEase : this._normalStopEase
        );
        const tl = new TimelineLite({
            onStart: () => {
                this.reelView.wantBounce = true;
            },
            onComplete: () => {
                this.reelState = ReelState.STOPPED;
            }
        });
        tl.add(this.reelView.step(this.addNextNewSymbol(TheRaveSymbolState.NORMAL), () => this.removeBottomSymbol()));
        this.reelView.allSymbols.forEach((symbol) => {
            tl.add(symbol.getStateAnimation(TheRaveSymbolState.NORMAL, {
                duration: this.reelView.currentStepDuration,
                alpha: 1
            }, {duration: this.reelView.currentStepDuration, alpha: 0}), 0);
        });

        return tl;
    }

    // The stop offset is for features on the reel to prolong the stop. like if the server gives a half stacked symbol in the bottom, so we need to add more symbols to fill it up.
    public getSpinStopOffset(): number {
        if (this.reelState !== ReelState.SPIN) {
            return 0;
        }

        // check if there is unfinished stacked wilds in the bottom of the stop reel set.
        // When the offset is more than 0, we have to make sure to compensate it from the stop reel set.
        let spinStopOffset: number = 0;
        return Math.max(0, spinStopOffset - this._symbolsNumInData[4]);
    }

    public hasNearWin(): boolean {
        return TheRave.model.parsedGameData.nearWinBigWinReels[this.reelId] || TheRave.model.parsedGameData.nearWinReels[this.reelId];
    }

    public addStopDelayIfNotEnough(value: number, addToNextStopReel: boolean = true): void {
        // Logger.logDev(`reel ${this.reelId} go added delay ${value}, totalDelayBefore ${this._extraStopDelay}`);
        this._extraStopDelay = Math.max(value, this._extraStopDelay);
        // Logger.logDev(`totalDelayAfter ${this._extraStopDelay}`);
        if (addToNextStopReel && this._nextStopReel != null) {
            TheRave.reels[this._nextStopReel]?.addStopDelayIfNotEnough(value, true);
        }
    }

    // This is just estimated time
    public getDefaultStoppingTime(): number {
        return (this._symsNumTotal - 1) * this._defaultSpinStepDuration + this._normalStopStepDuration;
    }

    public tryStop(spinStopOffset: number, delay: number, quickStop: boolean, stopStartCallBack: () => void, nextStopReel: number | undefined): void {
        //Logger.logDev(`reel ${this.reelId} try to stop in delay ${delay}, quickStop = ${quickStop}`);
        //console.log(this.reelId + " tryStop : " + delay);
        this.isQuickStop = quickStop;

        if (this.reelState === ReelState.STOPPING) {
            return;
        }

        if (this._reelStopDelayTimer && this._reelStopDelayTimer.isActive()) {
            this._reelStopDelayTimer.pause();
            this._reelStopDelayTimer.kill();
        }

        if (this.isQuickStop) {
            this._extraStopDelay = 0;
        }

        const totalDelay: number = delay + this._extraStopDelay;
        this._extraStopDelay = 0;
        // Logger.logDev(`reel ${this.reelId} try to stop in extra delay ${this._extraStopDelay}, total delay ${totalDelay}`);
        if (totalDelay > 0) {
            //console.log(this.reelId + " : " + totalDelay);
            this._reelStopDelayTimer = TweenLite.to(this, totalDelay, {onComplete: () => this.tryStop(spinStopOffset, 0, quickStop, stopStartCallBack, nextStopReel)});
            return;
        }
        if (stopStartCallBack) {
            stopStartCallBack();
        }
        this.getReadyToStop(spinStopOffset);

    }

    private getReadyToStop(spinStopOffset: number): void {
        Logger.logDev(`reel ${this.reelId} ready to stop.`);
        if(this._readyToStopped){
            //This prevent from stop twice on same reel and the reel will move up one more step
            //console.log(this.reelId +" stop counter: " + this._stopCounter);
            return;
        }
        let stopReelSet = TheRave.model.reels[this.reelId].concat(),
            addDelayToNextReel = 0;
        //const unfinishedSpinningStackedSymbolTopFillUp:string[] = this.handleUnfinishedSpinningStackedSymbols();
        this._currentReelSet = this.makeUpStopReelSet(false, stopReelSet);

        const alreadyFilled = this._stopReelSetFilledBottomNum;
        const needToFill = this._symbolsNumInData[4] + spinStopOffset;

        if (alreadyFilled < needToFill) {
            this._currentReelSet = this._currentReelSet.concat(this.makeUpStopReelSetRandomBottomFillUp(needToFill - alreadyFilled));
        } else {
            spinStopOffset = alreadyFilled - this._symbolsNumInData[4];
        }
        this._currentReelSetPosition = this._currentReelSet.length;
        this._stopCounter = this._symsNumTotal + spinStopOffset - 1;

        this._readyToStopped = true;
        addDelayToNextReel += spinStopOffset * this._defaultSpinStepDuration;// TODO: change the speed to use the real speed. like near win
        // TODO: if(!this._reelPartAnimator.isQuickStop && this._reelId < (GameConfig.instance.REELS_NUM - 1)) {
        //     addDelayToNextReel += this.checkSlowMotionStopDelay();
        // }
        addDelayToNextReel += this.reelView.currentStepLeftTime;

        if (addDelayToNextReel > 0 && this._nextStopReel != null) {
            // Logger.logDev(`reel ${this.reelId} get ready to stop, add delay ${addDelayToNextReel} to next reels`);
            TheRave.reels[this._nextStopReel]?.addStopDelayIfNotEnough(addDelayToNextReel, true);
        }

        this.setNextState(ReelState.STOPPING);
    }

    public getSymbol(index: number, isDataIndex: boolean = false, getVisibleStackedSymbol: boolean = true): TheRaveSymbol {
        if (!isDataIndex) {
            index += this._symbolsNumInData[0] + this._symbolsNumInData[1];
        } else {
            index += this._symbolsNumInData[0];
        }
        const symbol = this.reelView.allSymbols[index];
        return symbol;
    }

    public replaceSymbol(index: number, newSymName: string): void {
        const allSymbols: TheRaveSymbol[] = this.reelView.allSymbols,
            symbol: TheRaveSymbol = allSymbols[index];
        const currentNewSymbol: TheRaveSymbol = this.createSymbol(newSymName, this.reelId, TheRaveSymbolState.NORMAL);
        TheRave.reels[this.reelId].reelView.addWinSymbol(symbol);
        currentNewSymbol.position.set(symbol.x, symbol.y);
        this.removeOldSymbol(currentNewSymbol, symbol, index);
    }

    public doubleXwayExpansion(index: number, newSymName: string, multiplier: number): TimelineMax {
        const newSymbols: TheRaveSymbol[] = [],
            XWHeight: number = 300,
            oldSymbols: TheRaveSymbol[] = [],
            xWaySymbolId: number = index + this._symbolsNumInData[0] + this._symbolsNumInData[1],
            allSymbols: TheRaveSymbol[] = this.reelView.allSymbols,
            initSymbolHeight: number = 150,
            otherSymbolHeight: number = 75,
            scatterSymbolHeight: number = initSymbolHeight,
            tl: TimelineMax = new TimelineMax();
        let topSymId: number = 1,
            topY: number = (topSymId - 1) * initSymbolHeight;

        //Move and scale xways symbol and all other active symbols
        for (let i: number = topSymId; i < 5; i++) {
            const expandTl: TimelineMax = new TimelineMax(),
                symbol: TheRaveSymbol = allSymbols[i],
                isXWaysSymbol: boolean = (i === xWaySymbolId),
                scaleDuration: number = TheRaveGameConfig.REEL_ENGINE_CONFIG.DURATION.EXPAND!,
                toHeight: number = (isXWaysSymbol ? XWHeight : (symbol.symbolName === "S" ? scatterSymbolHeight : otherSymbolHeight)),
                toY: number = topY + toHeight * 0.5;

            const currentNewSymbol: TheRaveSymbol = this.createSymbol(isXWaysSymbol ? newSymName : symbol.symbolName, this.reelId, TheRaveSymbolState.NORMAL, toHeight);
            newSymbols.push(currentNewSymbol);
            oldSymbols.push(symbol);

            if (isXWaysSymbol) {
                //expandTl.add(this.expandXwaySymbolAnimation(i, scaleDuration, symbol), 0);
                //expandTl.add(() => this.removeOldSymbolWithoutAdd(symbol, i), 0);
            } else {
                expandTl.add(() => symbol.updateTexture(), scaleDuration - 0.1);
                expandTl.to(symbol.position, {
                    y: toY,
                    duration: scaleDuration,
                    ease: TheRaveGameConfig.REEL_ENGINE_CONFIG.EASING.EXPAND!
                }, 0.03);
                expandTl.to(symbol.scale, {
                    y: MathHelper.roundToDecimals(toHeight / 150, 1),
                    duration: scaleDuration,
                    ease: TheRaveGameConfig.REEL_ENGINE_CONFIG.EASING.EXPAND!
                }, 0.03);
            }
            currentNewSymbol.position.set(symbol.x, toY);

            tl.add(expandTl, 0);
            topY += toHeight;
        }

        //update split symbols
        tl.add(() => {
            newSymbols.forEach((sym: TheRaveSymbol, symId: number) => this.removeOldSymbol(sym, oldSymbols[symId], symId + topSymId));
            newSymbols[xWaySymbolId - topSymId]!.addMultiplier(multiplier / newSymbols[xWaySymbolId - topSymId]!.symbolSize);
        });
        tl.add(() => {
        }, 2);
        return tl;
    }

    public removeXwaysSymbols(indexes: number [] = []): TimelineMax {
        const tl:TimelineMax = new TimelineMax();

        //console.log("SYMBOLS NUM IN DATA", this._symbolsNumInData);

        indexes.forEach((xWaySymbollIdx) => {
            const xWaySymbolId: number = xWaySymbollIdx + this._symbolsNumInData[0] + this._symbolsNumInData[1];
        });

        return tl;
    }

    public replaceXWaysSymbol(index: number, newSymName: string, multiplier: number): TimelineMax {
        const newSymbols: TheRaveSymbol[] = [],
            XWHeight: number = 300,
            oldSymbols: TheRaveSymbol[] = [],
            xWaySymbolId: number = index + this._symbolsNumInData[0] + this._symbolsNumInData[1],
            allSymbols: TheRaveSymbol[] = this.reelView.allSymbols,
            initSymbolHeight: number = 150,
            otherSymbolHeight: number = 75,
            scatterSymbolHeight: number = initSymbolHeight,
            tl: TimelineMax = new TimelineMax();
        let topSymId: number = 1,
            topY: number = (topSymId - 1) * initSymbolHeight;

        //Move and scale xways symbol and all other active symbols
        for (let i: number = topSymId; i < 4; i++) {
            const expandTl: TimelineMax = new TimelineMax(),
                symbol: TheRaveSymbol = allSymbols[i],
                isXWaysSymbol: boolean = (i === xWaySymbolId),
                scaleDuration: number = TheRaveGameConfig.REEL_ENGINE_CONFIG.DURATION.EXPAND!,
                toHeight: number = (isXWaysSymbol ? XWHeight : (symbol.symbolName === "S" ? scatterSymbolHeight : otherSymbolHeight)),
                toY: number = topY + toHeight * 0.5;

            const currentNewSymbol: TheRaveSymbol = this.createSymbol(isXWaysSymbol ? newSymName : symbol.symbolName, this.reelId, TheRaveSymbolState.NORMAL, toHeight);
            newSymbols.push(currentNewSymbol);
            oldSymbols.push(symbol);

            if (isXWaysSymbol) {
                expandTl.add(this.expandXwaySymbolAnimation(i, scaleDuration, symbol), 0);
            } else {
                expandTl.add(() => symbol.updateTexture(), scaleDuration - 0.1);
                expandTl.to(symbol.position, {
                    y: toY,
                    duration: scaleDuration,
                    ease: TheRaveGameConfig.REEL_ENGINE_CONFIG.EASING.EXPAND!
                }, 0.03);
                expandTl.to(symbol.scale, {
                    y: MathHelper.roundToDecimals(toHeight / 150, 1),
                    duration: scaleDuration,
                    ease: TheRaveGameConfig.REEL_ENGINE_CONFIG.EASING.EXPAND!
                }, 0.03);
            }
            currentNewSymbol.position.set(symbol.x, toY);

            tl.add(expandTl, 0);
            topY += toHeight;
        }
        if (TheRave.model.parsedGameData.enhancerReel[this.reelId]) {
            tl.add(TheRave.enhancerReels[this.reelId].reelView.scaleGlow((TheRave.model.reels[this.reelId + 5][0] === "XW" ? 2 : 0.5)), 0.03);
        }
        //update split symbols
        tl.add(() => {
            newSymbols.forEach((sym: TheRaveSymbol, symId: number) => this.removeOldSymbol(sym, oldSymbols[symId], symId + topSymId));
            newSymbols[xWaySymbolId - topSymId]!.addMultiplier(multiplier / newSymbols[xWaySymbolId - topSymId]!.symbolSize);
        });
        tl.add(() => {
        }, 1.5);
        return tl;
    }

    private expandXwaySymbolAnimation(i: number, scaleDuration: number, symbol: TheRaveSymbol): TimelineLite {
        let texturePath = `${symbol.symbolName}/${symbol.symbolName}`;
        const anchorY: number = (i === 1 ? 0 : (i === 2 ? 0.5 : 1)),
            animateSymbolBg: PIXI.Sprite = new PIXI.Sprite(TheRave.assetLoader.getTextures(`${texturePath}_bg`)[0]),
            animateSymbol: PIXI.Sprite = new PIXI.Sprite(TheRave.assetLoader.getTextures(`${texturePath}_${symbol.symbolHeight}_${symbol.symbolSize}`)[0]),
            blueOutline: PIXI.Sprite = new PIXI.Sprite(TheRave.assetLoader.getTextures("xWayGlow")[0]),
            frame: TimelineSprite = new TimelineSprite(TheRave.assetLoader.getTextures("xWayFrame")),
            reel: TheRaveReelController = TheRave.reels[this.reelId];
        animateSymbolBg.visible = false;
        animateSymbolBg.anchor.set(0.5, anchorY);

        animateSymbolBg.addChild(blueOutline);
        animateSymbolBg.addChild(animateSymbol);
        animateSymbolBg.addChild(frame);

        animateSymbol.anchor.set(0.5, 0.5);
        animateSymbol.position.set(0, 150 * (0.5 - anchorY));

        blueOutline.blendMode = PIXI.BLEND_MODES.ADD;
        blueOutline.anchor.set(0.5, 0.5);
        blueOutline.position.set(0, 150 * (0.5 - anchorY));
        blueOutline.scale.set(0.8, 0.4);

        frame.blendMode = PIXI.BLEND_MODES.ADD;
        frame.anchor.set(0.5, 0.5);
        frame.position.set(0, 150 * (0.5 - anchorY));
        frame.scale.set(1, 0.5);
        frame.hide();
        reel.reelView.addWinSymbol(symbol);
        TheRaveLayers.getLayer(Layers.FOREGROUND_TOP)?.addChild(animateSymbolBg);
        const position: PIXI.Point = reel.getSymbol(i, false, false).getStagePosition();
        animateSymbolBg.position.set(position.x, position.y + (anchorY - 1.5) * 150);

        return new TimelineLite().add([
            () => {
                animateSymbolBg.visible = true;
                symbol.visible = false;
            },
            TweenLite.to(animateSymbolBg.scale, scaleDuration, {
                y: 2,
                ease: TheRaveGameConfig.REEL_ENGINE_CONFIG.EASING.EXPAND!
            }),
            TweenLite.to(animateSymbol.scale, scaleDuration, {
                y: 0.5,
                ease: TheRaveGameConfig.REEL_ENGINE_CONFIG.EASING.EXPAND!
            }),
        ])
            .add([
                frame.getAnimationAutoShowHide(true, true),
                TweenLite.fromTo(blueOutline.scale, 0.1, {x: 0.9, y: 0.55}, {x: 0.92, y: 0.5, ease: Power3.easeOut})
            ])
            .add(() => {
                symbol.position.y += (2 - i) * 75;
                animateSymbolBg.visible = false;
                TheRaveLayers.getLayer(Layers.FOREGROUND_TOP)?.removeChild(animateSymbolBg);
            });
    }

    public removeOldSymbol(sym: TheRaveSymbol, oldSym: TheRaveSymbol, symId: number): void {
        if (oldSym.symbolSize > 1) {
            sym.addSplitSymbolAnimation(oldSym.symbolSize, true);
        }
        this.reelView.addSymbol(sym);
        this.reelView.allSymbols[symId] = sym;
        this.reelView.removeSymbol(oldSym);
        oldSym.onRemove();
    }

    public removeOldSymbolWithoutAdd(oldSym: TheRaveSymbol, symId: number): void {
        if (oldSym.symbolSize > 1) {
            //sym.addSplitSymbolAnimation(oldSym.symbolSize, true);
        }
        //this.reelView.addSymbol(sym);
        //this.reelView.allSymbols[symId] = sym;
        oldSym.visible = false;
        this.reelView.removeSymbol(oldSym);
        oldSym.onRemove();
    }

    public doubleXwRestore():void {
        console.log("REEL: " + this.reelId + " DOUBLE X-WAY RESTORE");
    }

    public removeOldSymbolOnFullWild(sym: TheRaveSymbol, oldSym: TheRaveSymbol, symId: number): void {
        this.reelView.addSymbol(sym);
        this.reelView.allSymbols[symId] = sym;
        this.reelView.removeSymbol(oldSym);
        this.reelView.allSymbols[1].y += TheRaveReelView.SYMBOL_HEIGHT * 1.5;
        [2,3].forEach((symIdX) => {
            this.reelView.allSymbols[symIdX].visible = false;
        });
        oldSym.onRemove();
    }

    public setLinkedNum(linkedNum: number, left:number): void {
        this._linkedNum = linkedNum;
        this._linkedMostLeftReel = left;
        this.reelView.addToLinkedContainer();
        this.reelView.resetMask(linkedNum, left);
    }

    public restoreXwaySymbols(index: number, newSymName: string, multiplier: number): void {
        const newSymbols: TheRaveSymbol[] = [],
            oldSymbols: TheRaveSymbol[] = [],
            XWHeight: number = 300,
            topSymId: number = 1,
            xWaySymbolId: number = index + topSymId,
            allSymbols: TheRaveSymbol[] = this.reelView.allSymbols,
            initSymbolHeight: number = 150,
            otherSymbolHeight: number = 75;

        let topY: number = (topSymId - 1) * initSymbolHeight;

        //Move and scale xways symbol and all other active symbols
        for (let i: number = 1; i < 4; i++) {
            const symbol: TheRaveSymbol = allSymbols[i],
                isXWaysSymbol: boolean = (i === xWaySymbolId),
                toHeight: number = (isXWaysSymbol ? XWHeight : otherSymbolHeight),
                toY: number = topY + toHeight * 0.5;
            const currentNewSymbol: TheRaveSymbol = this.createSymbol(isXWaysSymbol ? newSymName : symbol.symbolName, this.reelId, TheRaveSymbolState.NORMAL, toHeight);
            newSymbols.push(currentNewSymbol);
            oldSymbols.push(symbol);
            currentNewSymbol.position.set(symbol.x, toY);
            topY += toHeight;
        }
        if (TheRave.model.currentParsedData.enhancerReel[this.reelId]) {
            TheRave.enhancerReels[this.reelId].reelView.scaleGlow((TheRave.model.reels[this.reelId][2] === "XW" ? 2 : 0.5));
        }
        //update split symbols
        newSymbols.forEach((sym: TheRaveSymbol, symId: number) => this.removeOldSymbol(sym, oldSymbols[symId], symId + topSymId));
        newSymbols[xWaySymbolId - topSymId]!.addMultiplierOnRestore(multiplier);
    }
}